import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { translationsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Translation {
  _id: string;
  key: string;
  category: string;
  en: string;
  id: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

interface TranslationFormData {
  key: string;
  category: string;
  en: string;
  id: string;
  description?: string;
}

export const ManageTranslations: React.FC = () => {
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState<Translation | null>(null);
  const [formData, setFormData] = useState<TranslationFormData>({
    key: '',
    category: 'common',
    en: '',
    id: '',
    description: ''
  });

  // Fetch translations with pagination and filters
  const { data: translationsData, isLoading } = useQuery({
    queryKey: ['admin-translations', page, searchTerm, selectedCategory],
    queryFn: () => translationsAPI.getAdminTranslations({
      page,
      limit: 20,
      search: searchTerm || undefined,
      category: selectedCategory || undefined
    }),
  });

  // Fetch categories for filter dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ['translation-categories'],
    queryFn: () => translationsAPI.getCategories(),
  });

  // Create translation mutation
  const createMutation = useMutation({
    mutationFn: (data: TranslationFormData) => translationsAPI.createTranslation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-translations'] });
      queryClient.invalidateQueries({ queryKey: ['translation-categories'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Failed to create translation:', error);
    },
  });

  // Update translation mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TranslationFormData }) =>
      translationsAPI.updateTranslation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-translations'] });
      setIsModalOpen(false);
      setEditingTranslation(null);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Failed to update translation:', error);
    },
  });

  // Delete translation mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => translationsAPI.deleteTranslation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-translations'] });
    },
    onError: (error: any) => {
      console.error('Failed to delete translation:', error);
    },
  });

  const resetForm = () => {
    setFormData({
      key: '',
      category: 'common',
      en: '',
      id: '',
      description: ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingTranslation) {
      updateMutation.mutate({ id: editingTranslation._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (translation: Translation) => {
    setEditingTranslation(translation);
    setFormData({
      key: translation.key,
      category: translation.category,
      en: translation.en,
      id: translation.id,
      description: translation.description || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = (translation: Translation) => {
    if (window.confirm(`Are you sure you want to delete the translation "${translation.key}"?`)) {
      deleteMutation.mutate(translation._id);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTranslation(null);
    resetForm();
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory]);

  const translations = translationsData?.data?.data || [];
  const pagination = translationsData?.data?.pagination;
  const categories = categoriesData?.data || [];

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>Manage Translations</h1>
          <p>Create and manage multilingual content for the application</p>
        </div>
        <div className="admin-actions">
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary"
          >
            Add Translation
          </button>
          <button onClick={logout} className="btn-logout">
            Logout
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by key, English, or Indonesian text..."
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => {
            setSearchTerm('');
            setSelectedCategory('');
          }}
          className="btn-clear"
        >
          Clear Filters
        </button>
      </div>

      {/* Translations Table */}
      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading translations...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Category</th>
                <th>English</th>
                <th>Indonesian</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {translations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-data">
                    No translations found
                  </td>
                </tr>
              ) : (
                translations.map((translation: Translation) => (
                  <tr key={translation._id}>
                    <td>
                      <code>{translation.key}</code>
                    </td>
                    <td>
                      <span className="category-badge">
                        {translation.category}
                      </span>
                    </td>
                    <td className="text-content">{translation.en}</td>
                    <td className="text-content">{translation.id}</td>
                    <td className="description">
                      {translation.description || '-'}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(translation)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(translation)}
                        className="btn-delete"
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} translations
          </div>
          <div className="pagination-buttons">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="btn-pagination"
            >
              Previous
            </button>
            <span className="pagination-current">
              Page {page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.pages}
              className="btn-pagination"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal for Create/Edit Translation */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTranslation ? 'Edit Translation' : 'Add Translation'}</h2>
              <button onClick={handleCloseModal} className="modal-close">
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Key *</label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    placeholder="e.g., common.save"
                    required
                  />
                  <small>Use dot notation for hierarchical keys</small>
                </div>
                
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="common">common</option>
                    <option value="navigation">navigation</option>
                    <option value="map">map</option>
                    <option value="forms">forms</option>
                    <option value="messages">messages</option>
                    <option value="admin">admin</option>
                    <option value="poi">poi</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>English Translation *</label>
                <textarea
                  value={formData.en}
                  onChange={(e) => setFormData({ ...formData, en: e.target.value })}
                  placeholder="English text"
                  required
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Indonesian Translation *</label>
                <textarea
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  placeholder="Teks bahasa Indonesia"
                  required
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description for context"
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingTranslation ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
